import type { JSX } from 'react';
import { useState } from 'react';
import { Alert, Button, Checkbox, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLE_LABELS, ROLE_NAMES, callName, type RoleName } from '@pst/shared';
import { ApiError, api } from '../api';
import { LinkResult } from './LinkResult';

/**
 * Inviting a new person: an account is created only once they open the link
 * and choose their own password. Nobody else ever knows it.
 */
export function InviteModal({
  opened,
  onClose,
  projectId,
}: {
  opened: boolean;
  onClose: () => void;
  /** Preselects the project the new person joins. */
  projectId?: string;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState('');
  const [nickname, setNickname] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<RoleName>('projektmitarbeiter');
  const [project, setProject] = useState<string | null>(projectId ?? null);

  const projects = useQuery({ queryKey: ['projects'], queryFn: api.projects, enabled: opened });
  const mail = useQuery({ queryKey: ['mail-status'], queryFn: api.mailStatus, enabled: opened, staleTime: 60_000 });
  const [sendMail, setSendMail] = useState(true);
  const mailing = sendMail && mail.data?.configured === true;
  const invite = useMutation({
    mutationFn: () => api.invite({ displayName, nickname: nickname.trim() || null, email, role, projectId: project, sendMail: mailing }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invites'] }),
  });

  const close = (): void => {
    setDisplayName('');
    setNickname('');
    setEmail('');
    setRole('projektmitarbeiter');
    setProject(projectId ?? null);
    invite.reset();
    onClose();
  };

  const ready = displayName.trim().length > 0 && /.+@.+\..+/.test(email.trim()) && !invite.isPending;

  return (
    <Modal opened={opened} onClose={close} title="Person einladen" size="lg">
      {invite.isSuccess ? (
        <LinkResult
          issued={invite.data}
          purpose={
            invite.data.mailed
              ? `${displayName} bekommt die Einladung an ${email}. Beim Öffnen des Links wählt ${displayName} ein eigenes Passwort, danach ist das Konto aktiv.`
              : `Diesen Link an ${displayName} geben. Beim Öffnen wählt ${displayName} ein eigenes Passwort, danach ist das Konto aktiv.`
          }
          onDone={close}
        />
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (ready) invite.mutate();
          }}
        >
          <Stack>
            {invite.isError && (
              <Alert color="red">
                {invite.error instanceof ApiError ? invite.error.message : 'Die Einladung ist fehlgeschlagen.'}
              </Alert>
            )}
            <Group grow align="flex-start">
              <TextInput
                label="Name"
                placeholder="Vor- und Nachname"
                required
                data-autofocus
                value={displayName}
                onChange={(event) => setDisplayName(event.currentTarget.value)}
              />
              <TextInput
                label="Anzeigename"
                placeholder={callName(displayName, null) || 'Vorname'}
                value={nickname}
                onChange={(event) => setNickname(event.currentTarget.value)}
              />
            </Group>
            <TextInput
              label="E-Mail-Adresse"
              description="Dient als Anmeldename."
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.currentTarget.value)}
            />
            <Select
              label="Rolle"
              required
              allowDeselect={false}
              data={ROLE_NAMES.map((name) => ({ value: name, label: ROLE_LABELS[name] }))}
              value={role}
              onChange={(value) => value && setRole(value as RoleName)}
              description={ROLE_HINTS[role]}
            />
            <Select
              label="Projekt"
              description="Optional. Die Person wird gleich Mitglied dieses Projekts; weitere lassen sich im Reiter „Team“ hinzufügen."
              clearable
              searchable
              placeholder="kein Projekt"
              data={(projects.data ?? []).map((entry) => ({
                value: entry.id,
                label: `${entry.number} · ${entry.name}`,
              }))}
              value={project}
              onChange={setProject}
            />
            {mail.data?.configured ? (
              <Checkbox
                label="Einladung per E-Mail senden"
                checked={sendMail}
                onChange={(event) => setSendMail(event.currentTarget.checked)}
              />
            ) : (
              <Text size="xs" c="dimmed">Für PST ist kein Mailversand eingerichtet; den Link geben Sie selbst weiter.</Text>
            )}
            <Button type="submit" disabled={!ready} loading={invite.isPending}>
              {mailing ? 'Einladen und E-Mail senden' : 'Einladungslink erzeugen'}
            </Button>
          </Stack>
        </form>
      )}
    </Modal>
  );
}

/** One sentence per role, so the choice does not need a manual. */
export const ROLE_HINTS: Readonly<Record<RoleName, string>> = {
  admin: 'Sieht alle Projekte, verwaltet die Nutzenden.',
  projektleiter: 'Legt Projekte an, verwaltet deren Team, setzt Basispläne.',
  projektmitarbeiter: 'Arbeitet in den zugeordneten Projekten mit, legt keine Projekte an.',
};
